import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getDeviceFingerprint } from "@/lib/deviceFingerprint";
import { useAuth } from "@/hooks/useAuth";

/**
 * Enforce que atalhos administrativos assinados (`?ads=<token>`) só funcionem
 * no dispositivo que os originou.
 *
 * Comportamento:
 * - Sem parâmetro `ads`: passa direto (proteção normal de admin cuida do resto).
 * - Com `ads`: chama `admin-shortcut-verify` enviando token + fingerprint atual.
 *   - OK: strip do parâmetro da URL e libera acesso.
 *   - Falha (assinatura, dispositivo diferente, revogado, não-admin): bloqueia
 *     com tela dedicada. Nenhum conteúdo administrativo é renderizado.
 */
const STORAGE_PREFIX = "zappy.admin.shortcut.ok.";

const AdminShortcutDeviceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get("ads");
  const alreadyVerifiedKey = token ? `${STORAGE_PREFIX}${token}` : null;

  const [state, setState] = useState<"idle" | "checking" | "ok" | "blocked">(() => {
    if (!token) return "ok";
    try {
      if (alreadyVerifiedKey && sessionStorage.getItem(alreadyVerifiedKey) === "1") return "ok";
    } catch { /* ignore */ }
    return "checking";
  });
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token || state !== "checking" || ranRef.current) return;
    if (!user) return; // aguarda auth; RoleGuard cuida do redirect se necessário
    ranRef.current = true;

    (async () => {
      try {
        const fp = getDeviceFingerprint();
        const { data, error } = await supabase.functions.invoke("admin-shortcut-verify", {
          body: { token, device_fp: fp },
        });
        if (error || !data?.ok) {
          setState("blocked");
          return;
        }
        try { sessionStorage.setItem(alreadyVerifiedKey!, "1"); } catch { /* ignore */ }
        // Remove o token da URL para não vazar em histórico/compartilhamento.
        const clean = new URLSearchParams(location.search);
        clean.delete("ads");
        const qs = clean.toString();
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : "", hash: location.hash },
          { replace: true },
        );
        setState("ok");
      } catch {
        setState("blocked");
      }
    })();
  }, [token, state, user, alreadyVerifiedKey, location.pathname, location.search, location.hash, navigate]);

  if (state === "checking") {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground font-body">Verificando atalho…</div>
      </div>
    );
  }

  if (state === "blocked") {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background text-foreground px-6">
        <div className="max-w-sm text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-destructive/15 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-destructive" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-display font-semibold">Atalho inválido para este dispositivo</h1>
          <p className="text-sm text-muted-foreground font-body">
            Este atalho administrativo foi gerado para outro dispositivo. Por segurança, o
            acesso via link foi bloqueado. Instale um novo atalho neste aparelho a partir do
            painel.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminShortcutDeviceGate;
